import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ClipboardSignature, Plus } from 'lucide-react'
import { api } from '../../../lib/api'
import { AntetFix } from '../../../components/ui/AntetFix'
import { Pastila } from '../../../components/ui/Pastila'
import { useAuthStore } from '../../../stores/auth'
import {
  LinkSofer,
  StarePv,
  cand,
  linkPvNou,
  useMasini,
  useOameni,
  type PvRand,
  type StatusPv,
} from './flotaComun'

/**
 * Flota → Procese-verbale: lista PV-urilor de predare-primire. Din ele se deriva cine a avut
 * fiecare masina, si deci cui ii merge fiecare alimentare (task CRM #2445).
 */
export function ProceseVerbalePage() {
  const [vehicleId, setVehicleId] = useState('')
  const [userId, setUserId] = useState('')
  const [status, setStatus] = useState<'' | StatusPv>('')
  const poateScrie = useAuthStore((s) => s.permissions.includes('operational.flota_pv:write'))
  const masini = useMasini()
  const oameni = useOameni()

  const lista = useQuery({
    queryKey: ['flota', 'pv', vehicleId, userId, status],
    queryFn: async () =>
      (
        await api.get<{ rows: PvRand[] }>('/flota/pv', {
          params: {
            vehicleId: vehicleId || undefined,
            userId: userId || undefined,
            status: status || undefined,
          },
        })
      ).data.rows,
  })

  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-center gap-2">
          <ClipboardSignature className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">Procese-verbale flotă</h1>
          <Link
            to="/operational/rompetrol"
            className="ml-2 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Flotă Rompetrol
          </Link>
          {poateScrie && (
            <Link
              to={linkPvNou({})}
              className="ml-auto flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600"
            >
              <Plus className="h-4 w-4" /> PV nou
            </Link>
          )}
        </div>
        <p className="text-sm text-slate-500">
          Cine a predat și cine a primit fiecare mașină. Din PV-urile semnate se știe cine a avut
          mașina la fiecare alimentare; fără PV, alimentarea rămâne „nealocată”.
        </p>
      </AntetFix>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <Filtru eticheta="Mașină">
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            {(masini.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.plate}
              </option>
            ))}
          </select>
        </Filtru>
        <Filtru eticheta="Om (predă sau primește)">
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toți</option>
            {(oameni.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Filtru>
        <Filtru eticheta="Stare">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | StatusPv)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            <option value="ciorna">Ciornă</option>
            <option value="semnat">Semnat</option>
            <option value="anulat">Anulat</option>
          </select>
        </Filtru>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Nr.</th>
              <th className="px-3 py-2">Mașină</th>
              <th className="px-3 py-2">Predare</th>
              <th className="px-3 py-2">Predă → Primește</th>
              <th className="px-3 py-2 text-right">Km</th>
              <th className="px-3 py-2">Stare</th>
              <th className="px-3 py-2">Introdus</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lista.isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-slate-500">
                  Se încarcă…
                </td>
              </tr>
            )}
            {lista.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-slate-500">
                  Niciun proces-verbal. Începe cu predările pe care le știi, în ordinea lor.
                </td>
              </tr>
            )}
            {lista.data?.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono">
                  <Link
                    to={`/operational/flota/pv/${p.id}`}
                    className="text-orange-700 hover:underline"
                  >
                    {p.numar ?? `ciornă #${p.id}`}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono font-medium">
                  <Link to={`/operational/flota/masini/${p.vehicleId}`} className="hover:underline">
                    {p.plate}
                  </Link>
                </td>
                <td className="px-3 py-2">{cand(p.predatLa)}</td>
                <td className="px-3 py-2">
                  <LinkSofer id={p.predatorId} nume={p.numePredator} /> →{' '}
                  <LinkSofer id={p.primitorId} nume={p.numePrimitor} />
                </td>
                <td className="px-3 py-2 text-right">{p.kilometraj.toLocaleString('ro-RO')}</td>
                <td className="space-x-1 px-3 py-2">
                  <StarePv status={p.status} />
                  {p.status === 'semnat' && !p.areScan && <Pastila ton="galben">fără scan</Pastila>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {p.numeCreator ?? '—'}, {cand(p.createdAt)}
                  {p.retroactiv && (
                    <span className="ml-1">
                      <Pastila ton="albastru">retroactiv</Pastila>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Filtru({ eticheta, children }: { eticheta: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] uppercase text-slate-400">{eticheta}</label>
      {children}
    </div>
  )
}
